import type {
  GetLegacyServicePickersResponse,
  GetPickersResponse,
} from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'

import {
  CLICKABLE_STEP_GUIDANCE_MODAL,
  STEP_COLUMNS,
} from '../CustomizationFunnel.constants'
import type { Prefixes as PickerPrefixes } from '../components/LargePicker/LargePicker.types'

import { getOptionIcon } from './getOptionIcon'
import { getStepIllustrations } from './getStepIllustrations'
import type { Option, Step } from './types'

export const mapOption = (
  {
    sublabel = undefined,
    ...option
  }:
    | GetPickersResponse['pickerGroups'][number]['items'][number]
    | GetLegacyServicePickersResponse['pickerGroups'][number]['items'][number],
  selectedOptionPriceAmount?: string,
  pickerPrefix: PickerPrefixes = 'circle',
): Option => {
  return {
    ...option,
    // Replacing \n with new line for the fake sold sublabel (returning escaped new line from the API)
    subLabel: sublabel ? sublabel.replaceAll('\\n', '\n') : undefined,
    style: {
      prefix: pickerPrefix,
    },
  }
}

export const mapStep = (
  product: GetProductResponse,
  group:
    | GetPickersResponse['pickerGroups'][number]
    | GetLegacyServicePickersResponse['pickerGroups'][number],
): Step => {
  const stepDesktopIllustration = getStepIllustrations(product, group)

  // Temp tracking that will be removed with no grade
  const gradesGuidanceTracking =
    group.id === 'grades'
      ? { zone: 'grade_picker', name: 'GUARANTEED_FUNCTIONAL' }
      : undefined
  const newBatteryGuidanceTracking =
    group.id === 'battery'
      ? { name: 'NEW_BATTERY', zone: 'battery_picker' }
      : undefined

  const selectedOptionPriceAmount = group.items.find((item) => item.selected)
    ?.price?.amount

  return {
    id: group.id,
    title: group.sectionTitle ?? group.label,
    titleEmphasis: null,
    trackingId: group.trackingId,
    options:
      group.items.map((option) =>
        mapOption(
          option,
          selectedOptionPriceAmount,
          getOptionIcon(group, option),
        ),
      ) ?? [],
    desktopIllustration: stepDesktopIllustration[group.id] ?? null,
    columns: STEP_COLUMNS[group.id] ?? undefined,
    guidance:
      // Associates modals to clickable guidance cards based on the step/group id
      // eslint-disable-next-line no-nested-ternary
      group.guidanceCard && CLICKABLE_STEP_GUIDANCE_MODAL[group.id]
        ? {
            modalName: CLICKABLE_STEP_GUIDANCE_MODAL[group.id],
            ...(group.guidanceCard.icon?.name
              ? { icon: group.guidanceCard.icon.name }
              : {}),
            ...(group.guidanceCard.icon?.url
              ? { image: group.guidanceCard.icon.url }
              : {}),
            label: group.guidanceCard.text,
            clickable: group.guidanceCard.clickable || false,
            tracking:
              gradesGuidanceTracking ?? newBatteryGuidanceTracking ?? undefined,
          }
        : Boolean(group.guidanceCard) && !group.guidanceCard?.clickable
          ? {
              label: group.guidanceCard?.text ?? '',
              clickable: false,
              tracking: undefined,
              icon: group.guidanceCard?.icon?.name ?? undefined,
              image: group.guidanceCard?.icon?.url ?? undefined,
            }
          : undefined,
    sideContentV2: group.sideContentV2,
  } satisfies Step
}

export const mapServiceStep = (
  product: GetProductResponse,
  group: GetLegacyServicePickersResponse['pickerGroups'][number],
): Step => {
  return {
    type: 'service',
    ...mapStep(product, group),
  }
}
